import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const ease = [0.16, 1, 0.3, 1];

function Nav() {
  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/85 backdrop-blur border-b border-[#1A1A1A]"
      data-testid="bedrock-nav"
    >
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14 h-14 grid grid-cols-12 items-center font-mono text-[10px] tracking-[0.3em] uppercase">
        <Link to="/designs" className="col-span-3 text-[#F2F0EB]">
          [ Credit / Stone ] — Bedrock
        </Link>
        <div className="col-span-6 hidden md:flex items-center justify-center gap-10 text-[#8C8C8C]">
          <a href="#data">§ Data</a>
          <a href="#editorial">§ Editorial</a>
          <a href="#colophon">§ Colophon</a>
        </div>
        <div className="col-span-3 flex items-center justify-end gap-4">
          <span className="text-[#8C8C8C] hidden sm:inline">v.1 · MMXXVI</span>
          <Link to="/designs" className="px-3 py-1.5 border border-[#262626] rounded-full hover:bg-white/5">
            ← Variants
          </Link>
        </div>
      </div>
    </header>
  );
}

const rates = [
  ["USD-T+0",   "0.040", "+0.002"],
  ["EUR-TRSY",  "4.214", "+0.014"],
  ["GBP-CR-A",  "5.862", "-0.008"],
  ["JPY-VLT",   "0.610", "+0.004"],
  ["CHF-RES",   "1.921", "+0.001"],
  ["SGD-CL-B",  "3.747", "-0.012"],
  ["USD-PCRD",  "8.926", "+0.041"],
  ["EUR-STR-B", "6.083", "+0.022"],
];

const radial = [
  { d: 0,    label: "Origination" },
  { d: 60,   label: "Underwriting" },
  { d: 120,  label: "Custody" },
  { d: 180,  label: "Clearing" },
  { d: 240,  label: "Reserve" },
  { d: 300,  label: "Yield" },
];

export default function Bedrock() {
  return (
    <main className="min-h-screen bg-[#050505] text-[#F2F0EB] grain" data-testid="bedrock-page">
      <Nav />

      {/* HERO — radial monolith */}
      <section className="pt-28 pb-24 relative overflow-hidden">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-x-6 gap-y-10">
            <div className="col-span-12 md:col-span-3 font-mono text-[10px] tracking-[0.3em] uppercase text-[#8C8C8C]">
              <div>§ 01 · Hero</div>
              <div className="mt-2">Layout / 12-col Swiss</div>
              <div className="mt-2">Date / MMXXVI.IV.XXVI</div>
            </div>

            <motion.h1
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 1.3, ease }}
              className="col-span-12 md:col-span-9 font-display uppercase leading-[0.85] tracking-tight text-[10vw] md:text-[7.5vw]"
            >
              Finance, set in
              <br />
              <span className="text-[#F2F0EB]/55">type and</span> stone.
            </motion.h1>
          </div>

          {/* Radial monolith */}
          <div className="relative mt-20 grid grid-cols-12 gap-6">
            <div className="col-span-12 lg:col-span-8 relative aspect-square max-w-[640px] mx-auto w-full">
              {/* rings */}
              <div className="absolute inset-0 rounded-full border border-white/10" />
              <div className="absolute inset-[8%] rounded-full border border-white/[0.06]" />
              <div className="absolute inset-[18%] rounded-full border border-white/[0.04]" />

              {/* monolith center */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                className="absolute inset-[28%] rounded-full overflow-hidden border border-white/10"
              >
                <img
                  src="https://images.unsplash.com/photo-1634382851346-890ee66f08b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxibGFjayUyMHN0b25lJTIwbW9ub2xpdGh8ZW58MHx8fGJsYWNrfDE3NzcyMTczMjd8MA&ixlib=rb-4.1.0&q=85"
                  alt=""
                  className="w-full h-full object-cover grayscale"
                />
              </motion.div>

              {/* radial labels */}
              {radial.map((r, i) => {
                const angle = (r.d * Math.PI) / 180;
                const x = 50 + 48 * Math.cos(angle);
                const y = 50 + 48 * Math.sin(angle);
                return (
                  <motion.div
                    key={r.label}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, ease, delay: 0.2 + i * 0.08 }}
                    className="absolute font-mono text-[10px] tracking-[0.3em] uppercase text-[#F2F0EB]"
                    style={{
                      left: `${x}%`,
                      top: `${y}%`,
                      transform: "translate(-50%, -50%)",
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#F2F0EB]" />
                      {r.label}
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <div className="col-span-12 lg:col-span-4 flex flex-col justify-center">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#8C8C8C] mb-4">
                Abstract
              </div>
              <p className="font-body text-base text-[#F2F0EB]/85 leading-relaxed first-letter:font-display first-letter:text-7xl first-letter:float-left first-letter:mr-3 first-letter:leading-[0.8]">
                Credit Stone is a single, programmable substrate for institutional
                finance. Origination, custody, clearing and yield resolve to one
                ledger event. The result is capital that behaves like type on a
                page: deliberate, legible, ordered.
              </p>
              <div className="mt-6 flex items-center gap-3">
                <button className="font-mono text-[11px] tracking-[0.25em] uppercase px-5 py-3 rounded-full bg-[#F2F0EB] text-[#050505]">
                  Request Brief
                </button>
                <button className="font-mono text-[11px] tracking-[0.25em] uppercase px-5 py-3 rounded-full border border-white/15">
                  PDF Folio
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DATA TABLE */}
      <section id="data" className="border-y border-[#1A1A1A] py-20">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-3 font-mono text-[10px] tracking-[0.3em] uppercase text-[#8C8C8C]">
            <div>§ 02 · Data</div>
            <div className="mt-2">Live, Tier-1</div>
            <div className="mt-2">Updated · 03s ago</div>
          </div>
          <div className="col-span-12 md:col-span-9">
            <div className="grid grid-cols-12 font-mono text-[10px] tracking-[0.3em] uppercase text-[#8C8C8C] pb-3 border-b border-[#1A1A1A]">
              <div className="col-span-1">#</div>
              <div className="col-span-5">Instrument</div>
              <div className="col-span-3 text-right">Rate</div>
              <div className="col-span-3 text-right">Δ 24h</div>
            </div>
            <ul>
              {rates.map((r, i) => (
                <motion.li
                  key={r[0]}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-10% 0px" }}
                  transition={{ duration: 0.6, ease, delay: i * 0.04 }}
                  className="grid grid-cols-12 py-4 border-b border-[#141414] hover:bg-white/[0.02]"
                >
                  <div className="col-span-1 font-mono text-xs text-[#8C8C8C]">{String(i + 1).padStart(2, "0")}</div>
                  <div className="col-span-5 font-mono text-sm text-[#F2F0EB]">{r[0]}</div>
                  <div className="col-span-3 font-display text-xl text-right">{r[1]}<span className="text-sm text-[#8C8C8C] ml-1">%</span></div>
                  <div className={`col-span-3 font-mono text-sm text-right ${r[2].startsWith("-") ? "text-[#9a9a9a]" : "text-[#F2F0EB]/90"}`}>{r[2]}</div>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* EDITORIAL — justified columns */}
      <section id="editorial" className="py-28">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-3 font-mono text-[10px] tracking-[0.3em] uppercase text-[#8C8C8C]">
            <div>§ 03 · Editorial</div>
            <div className="mt-2">Note from the architects</div>
          </div>
          <div className="col-span-12 md:col-span-9 grid grid-cols-1 md:grid-cols-2 gap-10">
            <p className="font-body text-base text-[#F2F0EB]/85 leading-relaxed text-justify hyphens-auto">
              We assembled Credit Stone from three observations. First, that
              correspondent banking is a tax on velocity. Second, that
              custodianship is a function of paper, not of trust. Third, that
              the absence of a single ledger has cost the modern treasury more
              than any market has.
            </p>
            <p className="font-body text-base text-[#8C8C8C] leading-relaxed text-justify hyphens-auto">
              The platform you are looking at collapses those three frictions
              into one programmable surface. There are no intermediaries, no
              statements, no end-of-day. Capital moves the way you instruct it,
              and a record is left behind in stone — auditable, attestable, and
              quietly indifferent to the cycle.
            </p>
          </div>
        </div>
      </section>

      {/* COLOPHON */}
      <footer id="colophon" className="border-t border-[#1A1A1A] py-12">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14 grid grid-cols-12 gap-6 font-mono text-[10px] tracking-[0.3em] uppercase text-[#8C8C8C]">
          <div className="col-span-6 md:col-span-3">© Credit/Stone Holdings AG</div>
          <div className="col-span-6 md:col-span-3">Zürich · Singapore</div>
          <div className="col-span-6 md:col-span-3">Type · Gloock / Manrope / Mono</div>
          <div className="col-span-6 md:col-span-3 md:text-right">Issue 001 · MMXXVI</div>
        </div>
      </footer>
    </main>
  );
}
